/* RocketFuel quick add: a bare minimum buy box from the product page in a popup (Oct 2026).
   Shown: gallery (product page slider), title and price, variant tiles, box tiers with the
   one-time Duo / Trio prices, quantity and Add to cart. Left out: see LEFT_OUT below.

   Horizon's quick add copies the product page's product block ([data-product-grid-content])
   into #quick-add-modal-content and then shows a stripped version of it: its quick-add styles
   (snippets/quick-add-modal-styles.liquid) hide every block except title, variant picker and
   buy buttons, stack the gallery images under each other and switch the slider off. The
   copied script tags never run either, so the RF blocks (volume tiers, delivered price,
   shipping box, gallery helpers) stayed dead.

   This file shows the real product page instead, from the same source:
   1. The content element drops Horizon's class .quick-add-modal__content (and gets .rf-qa),
      so those overrides stop applying and the product page's own styles take over. The popup
      frame (size, columns, what is left out) lives in snippets/rf-quick-add.liquid.
   2. It also gets the class .shopify-section: every product page script finds its buy box
      with closest('.shopify-section') (volume tiers, price header, regular delivery,
      quantity sync). Without it they fell back to the whole page and picked up the product
      cards' forms on collection pages.
   3. The product page scripts inside the copied content run, in their original order. For
      a different product than last time the product page files run again and the page gets
      a 'shopify:section:load' event (detail.sectionId 'rf-quick-add'), the same signal the
      scripts already handle for re-rendered sections, so they set up the new content.
      Files the page itself already loaded (cart drawer, theme) are never run twice.
   4. Phones get the same layout as desktop; Horizon rebuilt a mini form there.
   Any change to the product page (blocks, styles, scripts) therefore shows up in the popup
   on its own. If this file fails to load, Horizon's own quick add keeps working as before.

   On product pages a quick add "Choose" button (recommendation rows) opens that product's
   page instead: a second copy of the product page scripts next to the main buy box would
   clash on shared block ids. */
import { morph } from '@theme/morph';

const ran = new WeakSet();
const loaded = new Set();
let lastProduct = null;

/** Runs the scripts in the copied product page content, like the product page does.
    fresh: a different product than last time, so product page files run again too. */
async function runScripts(root, fresh) {
  const pageSrc = new Set(
    [...document.querySelectorAll('script[src]')].filter((s) => !root.contains(s)).map((s) => s.src)
  );

  for (const old of [...root.querySelectorAll('script')]) {
    if (ran.has(old) || !old.isConnected) continue;
    ran.add(old);

    const type = (old.getAttribute('type') || '').trim().toLowerCase();
    if (type && type !== 'module' && type !== 'text/javascript' && type !== 'application/javascript') continue;

    if (old.src) {
      if (pageSrc.has(old.src)) continue;
      // Modules only ever run once per page; classic files again for a new product.
      if (loaded.has(old.src) && (!fresh || type === 'module')) continue;
      loaded.add(old.src);
    }

    const script = document.createElement('script');
    for (const { name, value } of [...old.attributes]) script.setAttribute(name, value);
    ran.add(script);

    if (old.src) {
      // Classic files run in order (later inline scripts may need them); never wait more than 4s.
      const done = new Promise((resolve) => {
        script.onload = resolve;
        script.onerror = resolve;
        setTimeout(resolve, 4000);
      });
      old.replaceWith(script);
      if (type !== 'module') await done;
    } else {
      script.textContent = old.textContent;
      old.replaceWith(script);
    }
  }
}

/** Same as Horizon: pre-select the variant picked on the product card. */
function syncVariant(component, modalContent) {
  const card = component.closest('product-card');
  const id = card && typeof card.getSelectedVariantId === 'function' ? card.getSelectedVariantId() : null;
  if (!id) return;
  for (const input of modalContent.querySelectorAll('input[type="radio"][data-variant-id]')) {
    if (input instanceof HTMLInputElement && input.dataset.variantId === String(id) && !input.checked) {
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      break;
    }
  }
}

/* Bare minimum popup (16 Oct 2026): gallery, title and price, variant tiles, box tiers,
   quantity and Add to cart. Everything else on the product page stays out. Removing the
   regular delivery switch (not just hiding it) also makes the box tiers show the one-time
   Duo / Trio prices and keeps the add to cart a one-time add. */
const LEFT_OUT = [
  'rf-abo-toggle', // regular delivery
  'rf-delivered-price',
  'rf-shipping', // shipping box
  'dialog.rf-policy',
  '.rf-trust', // secure checkout / payment row
  '.rf-acc', // product info accordions
  "[id$='__rf_pdp_intro']", // intro text and bullets
].join(',');

function stripExtras(root) {
  root.querySelectorAll(LEFT_OUT).forEach((el) => el.remove());
}

/* The theme re-renders parts of the buy box after a variant change; keep the left-out
   blocks out when they come back. */
let guarded = false;
function guard(modalContent) {
  if (guarded) return;
  guarded = true;
  new MutationObserver(() => {
    if (modalContent.querySelector(LEFT_OUT)) stripExtras(modalContent);
  }).observe(modalContent, { childList: true, subtree: true });
}

customElements.whenDefined('quick-add-component').then(() => {
  const QuickAdd = customElements.get('quick-add-component');
  if (!QuickAdd || QuickAdd.prototype.rfQuickAdd) return;
  QuickAdd.prototype.rfQuickAdd = true;

  /** Replaces Horizon's version: the whole product page content, same layout on phones. */
  QuickAdd.prototype.updateQuickAddModal = async function (productGrid) {
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!productGrid || !modalContent) return;

    // Classes first, so product page elements find their buy box as soon as they connect.
    modalContent.classList.remove('quick-add-modal__content');
    modalContent.classList.add('rf-qa', 'shopify-section');
    modalContent.setAttribute('view-event-payload', productGrid.getAttribute('view-event-payload') || '');

    stripExtras(productGrid);
    morph(modalContent, productGrid);
    guard(modalContent);

    syncVariant(this, modalContent);
    modalContent.scrollTop = 0;

    const product = this.productPageUrl.split('?')[0];
    const fresh = product !== lastProduct;
    lastProduct = product;
    runScripts(modalContent, fresh).then(() => {
      if (fresh) {
        document.dispatchEvent(new CustomEvent('shopify:section:load', { detail: { sectionId: 'rf-quick-add' } }));
      }
      // The server renders the tiles with regular delivery prices when the cart is on regular
      // delivery; without the switch here they get the one-time Duo / Trio prices.
      setTimeout(() => {
        modalContent.querySelectorAll('rf-volume-tiers').forEach((tiers) => {
          if (!tiers.currentPrice || typeof tiers.updatePrices !== 'function') return;
          tiers.updatePrices(tiers.currentPrice);
          tiers.markTier(tiers.currentQty);
        });
      }, 50);
    });
  };
});

/* Product pages: "Choose" opens the product page (see the note at the top). */
document.addEventListener(
  'click',
  (event) => {
    const target = event.target;
    const button = target instanceof Element ? target.closest('.quick-add__button--choose') : null;
    if (!button) return;
    const main = document.getElementById('MainContent');
    if (!main || (main.dataset.template || '').indexOf('product') !== 0) return;
    const component = button.closest('quick-add-component');
    const url = component && component.productPageUrl;
    if (!url) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = url;
  },
  true
);
